import { useEffect, useRef, useState } from 'react';
import type { WorkspaceMember } from '@forecastify/shared';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Button, Icon, PROSE, type IconName } from '@forecastify/ui';
import { useT, type MessageKey } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The comment editor. C19, and R-16's deferral finally paid.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A REAL EDITOR LIBRARY, WHICH IS WHAT R-16 SAID THIS WOULD TAKE.** It shipped as a
 * textarea whose `**bold**` became markup only at SUBMIT — so nothing was formatted while it was
 * being written, which is the complaint that brought this row back. The mockup builds its editor on
 * `document.execCommand`, deprecated in every browser and wrapped in a per-command `try/catch` in
 * its own toolbar because of it; R-16 refused to ship that as the primary way people write here.
 *
 * TipTap (ProseMirror) is the choice, and the reason is the ask: Atlassian's own editor is
 * ProseMirror-based, so *"like Jira's"* is nearest here. Recorded in `decisions.md`.
 *
 * ⚠️⚠️ **THE SERVER'S ALLOW-LIST IS THE CONSTRAINT, NOT THIS TOOLBAR.** Every mark and node below
 * exists in `sanitiseHtml`'s set. A button producing anything else would write markup the save
 * silently strips — a formatting control that appears to work and does not, which is worse than
 * one that is missing.
 *
 * ⚠️ **No AI.** The mockup's *Improve writing* and the Loom strip are not built and are not wanted.
 */

/**
 * ⚠️ **The `@` fragment under the caret, or null.** Unchanged from the textarea version — it works
 * on the editor's PLAIN TEXT, which is what the mention contract is keyed on: the server takes ids
 * and the renderer highlights by name, so what has to be true is that the name is in the text.
 */
export function mentionQuery(text: string, caret: number): string | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at === -1) return null;

  const preceding = at === 0 ? ' ' : before[at - 1];
  if (preceding !== undefined && !/[\s(]/.test(preceding)) return null;

  const fragment = before.slice(at + 1);
  if (fragment.includes('\n')) return null;
  /* Two words at most: "Farkas András" matches, a whole sentence does not. */
  if (fragment.split(' ').length > 2) return null;
  return fragment;
}

/**
 * Which picked mentions survive to the wire.
 *
 * ⚠️⚠️ **PICKING SOMEBODY AND THEN DELETING THEIR NAME MUST NOT NOTIFY THEM.** Changing your mind
 * is an ordinary edit, and a mention that mails somebody about a comment which does not name them
 * is the worst kind of wrong: invisible to the writer and inexplicable to the reader.
 *
 * ⚠️ **Exported and pure, because the interaction that produces it CANNOT BE TESTED IN JSDOM.**
 * `userEvent` edits a `contenteditable` in the DOM directly; ProseMirror never sees a transaction,
 * so its document stays empty, no `update` fires and nothing this component does is reachable from
 * a simulated keystroke. The rule is the part worth guarding, so it lives here where a test can
 * reach it — and the typing itself is checked in a real browser.
 */
export function keptMentions(
  text: string,
  picked: readonly WorkspaceMember[],
): readonly WorkspaceMember[] {
  return picked.filter((person) => text.includes(`@${person.displayName}`));
}

/** The members whose name matches — five at most, which is what fits without a scroll. */
export function mentionMatches(
  people: readonly WorkspaceMember[],
  query: string,
): readonly WorkspaceMember[] {
  const needle = query.trim().toLocaleLowerCase();
  const matches =
    needle === ''
      ? people
      : people.filter((person) => person.displayName.toLocaleLowerCase().includes(needle));
  return matches.slice(0, 5);
}

/**
 * One toolbar control.
 *
 * ⚠️ **`aria-pressed`, not a colour.** Whether bold is on is state, and a background tint says it
 * to exactly the people who can see it — `design-system/readme.md`'s rule, and the reason this is a
 * toggle button rather than a styled div.
 */
function ToolButton({
  icon,
  label,
  active,
  disabled,
  onClick,
}: {
  readonly icon: IconName;
  readonly label: string;
  readonly active?: boolean;
  readonly disabled?: boolean;
  readonly onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label={label}
      title={label}
      aria-pressed={active}
      disabled={disabled}
      className={active === true ? 'bg-accent text-foreground' : undefined}
      /* ⚠️ The editor must not lose the selection when the button takes focus, or every command
       * would apply to an empty range. */
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onClick={onClick}
    >
      <Icon name={icon} size={14} />
    </Button>
  );
}

/** The toolbar, in the mockup's order minus the AI and the video strip. */
function Toolbar({
  editor,
  t,
}: {
  readonly editor: Editor;
  readonly t: (key: MessageKey) => string;
}) {
  const marks: { icon: IconName; label: MessageKey; name: string; run: () => void }[] = [
    {
      icon: 'bold',
      label: 'comment.bold',
      name: 'bold',
      run: () => editor.chain().focus().toggleBold().run(),
    },
    {
      icon: 'italic',
      label: 'comment.italic',
      name: 'italic',
      run: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      icon: 'strikethrough',
      label: 'comment.strike',
      name: 'strike',
      run: () => editor.chain().focus().toggleStrike().run(),
    },
    {
      icon: 'code',
      label: 'comment.code',
      name: 'code',
      run: () => editor.chain().focus().toggleCode().run(),
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b px-1.5 py-1">
      {marks.map((mark) => (
        <ToolButton
          key={mark.name}
          icon={mark.icon}
          label={t(mark.label)}
          active={editor.isActive(mark.name)}
          onClick={mark.run}
        />
      ))}

      <span className="bg-border mx-1 h-4 w-px" aria-hidden />

      <ToolButton
        icon="heading-2"
        label={t('comment.heading')}
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      />
      <ToolButton
        icon="list"
        label={t('comment.bullets')}
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolButton
        icon="list-ordered"
        label={t('comment.numbers')}
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <ToolButton
        icon="quote"
        label={t('comment.quote')}
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      />
      <ToolButton
        icon="braces"
        label={t('comment.codeBlock')}
        active={editor.isActive('codeBlock')}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      />

      <span className="bg-border mx-1 h-4 w-px" aria-hidden />

      <LinkButton editor={editor} t={t} />

      <span className="flex-1" />

      {/* ⚠️ Undo and redo are the EDITOR's history, not the browser's — a contenteditable's native
          undo does not know about a command applied through the model. */}
      <ToolButton
        icon="undo"
        label={t('comment.undo')}
        disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      />
      <ToolButton
        icon="redo"
        label={t('comment.redo')}
        disabled={!editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      />
    </div>
  );
}

/**
 * The link control.
 *
 * ⚠️⚠️ **NO `window.prompt`, WHICH IS WHAT THE MOCKUP USES.** A native prompt cannot be styled,
 * cannot be translated, is blocked outright in some embedding contexts, and blocks the whole tab
 * while it is open. The URL is asked for inline, in the toolbar, where the selection stays visible.
 *
 * ⚠️ **An empty submit REMOVES the link** rather than setting one to nothing — which is the only
 * way to unlink from a keyboard here, and what the button reads as when the caret is already in a
 * link.
 */
function LinkButton({
  editor,
  t,
}: {
  readonly editor: Editor;
  readonly t: (key: MessageKey) => string;
}) {
  const [open, setOpen] = useState(false);
  const [href, setHref] = useState('');

  const commit = (): void => {
    const url = href.trim();
    setOpen(false);
    setHref('');
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    /**
     * ⚠️ **A bare `example.com` becomes `https://example.com`.** Left alone it is a RELATIVE url,
     * so the link would point inside this app and 404 — and the server's scheme allow-list would
     * have let it through, because a relative path is not a dangerous scheme.
     */
    const absolute = /^[a-z][a-z0-9+.-]*:/i.test(url) ? url : `https://${url}`;
    editor.chain().focus().extendMarkRange('link').setLink({ href: absolute }).run();
  };

  return (
    <>
      <ToolButton
        icon="link"
        label={t('comment.link')}
        active={editor.isActive('link')}
        onClick={() => {
          setHref(
            typeof editor.getAttributes('link').href === 'string'
              ? String(editor.getAttributes('link').href)
              : '',
          );
          setOpen((current) => !current);
        }}
      />
      {open ? (
        <input
          autoFocus
          value={href}
          onChange={(event) => {
            setHref(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
            if (event.key === 'Escape') {
              /* Handled — the modal around it stays open (`useModalSurface`). */
              event.preventDefault();
              setOpen(false);
            }
          }}
          onBlur={commit}
          placeholder="https://…"
          aria-label={t('comment.link')}
          className="border-input focus-visible:ring-ring/40 h-7 min-w-0 flex-1 rounded-md border px-2 text-[12px] outline-none focus-visible:ring-[3px]"
        />
      ) : null}
    </>
  );
}

/**
 * ⚠️⚠️ **THE SAME RULES STYLE THE EDITOR AND THE SAVED COMMENT.** TipTap ships no CSS and the app's
 * reset flattens `h2`, `ul`, `blockquote` and `pre` — so without these a heading and a bullet list
 * render as plain paragraphs, which is *"formatting shown immediately"* failing quietly. Two copies
 * would let what you typed and what you read afterwards drift apart, which is the same class of bug
 * as two allow-lists.
 *
 * ⚠️ **The rules themselves moved to `@forecastify/ui`'s `PROSE` in C48**, when the Agent's replies
 * became the product's second surface that renders markup it did not author. They cannot be imported
 * from here: this module imports TipTap, and importing a constant out of it would put ProseMirror in
 * the chat's chunk. The alias stays so the two call sites in this folder still read as comment code.
 */
export const COMMENT_PROSE = PROSE;

export function CommentComposer({
  people = [],
  onSubmit,
  initialHtml,
  submitLabel,
  onCancel,
  footer,
  allowFiles = true,
  commitOnBlur = false,
  incomingFiles,
  onIncomingTaken,
  onChange,
  placeholder,
  label,
}: {
  /** The workspace's members, for the `@` picker. Empty when the caller cannot read `/members`. */
  readonly people?: readonly WorkspaceMember[];
  readonly onSubmit: (
    html: string,
    mentionMemberIds: readonly string[],
    files?: readonly File[],
  ) => void;
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE SAME COMPOSER, EDITING — item 73.                            │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * *"Comments can be edited and deleted."* The endpoints have existed since C16 —
   * `PUT :id/comments/:commentId` and its sibling — and only the controls were missing.
   *
   * ⚠️⚠️ **THIS COMPOSER RATHER THAN A PLAIN TEXTAREA, AND THE MENTIONS ARE WHY.** An edit has to
   * be able to add or remove an `@`, and the mention set is what the server NOTIFIES on: a second
   * editor that could not produce one would make *"fix the typo in the name I tagged"* impossible,
   * and a comment edited in a textarea would lose its formatting on the way through.
   */
  readonly initialHtml?: string;
  /** Already translated. Defaults to *Küldés*. */
  readonly submitLabel?: string;
  /** ⚠️ Given, a cancel button appears. Editing needs a way out that is not saving. */
  readonly onCancel?: () => void;
  /**
   * ⚠️ **`false` while editing.** An attachment belongs to the comment and is managed BESIDE it
   * (`Attachments`, C22) — staging one here on an edit would offer a second, quieter path to the
   * same list, and the two would disagree about what had been uploaded.
   */
  readonly allowFiles?: boolean;
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ AN INLINE FIELD: IT COMMITS WHEN FOCUS LEAVES, AND HAS NO BUTTONS.   │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * Item 3, second pass, 15 Sep — *"still has a save button instead of saving simply when the user
   * exits the input field like every other inline editor"*. The first pass made the description
   * click-to-edit and kept the composer whole, Save button and all, which left it the only field on
   * the task screen that has to be committed while the assignee, the status and the dates beside it
   * all write on change.
   *
   * ⚠️⚠️ **`focusout` WITH A `relatedTarget` TEST, NOT `blur` ON THE EDITOR.** The toolbar, the
   * mention picker and the editor are three focusable things inside one control: a plain blur fires
   * every time somebody reaches for **B**, so pressing bold would save and close the editor under
   * their hand. The test is whether focus went somewhere still inside this component.
   *
   * ⚠️ **Escape reverts and leaves.** Committing on the way out means there is otherwise no way to
   * abandon an edit, and a field you cannot back out of is one people stop clicking into.
   *
   * ⚠️ Comments keep the button: a comment is *sent*, and something that leaves the page when you
   * look away is not a thing you would ever trust with a paragraph you are still composing.
   */
  readonly commitOnBlur?: boolean;
  /**
   * ┌────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ BUTTONS AND BLUR ARE TWO QUESTIONS, AND `commitOnBlur` ANSWERED BOTH.        │
   * └────────────────────────────────────────────────────────────────────┘
   *
   * An inline field used to mean *saves on blur AND has no footer*, because item 3 asked for the
   * second and the first came with it. Feedback round 4 item 13 asks for both at once on the
   * description: an explicit **Mentés / Mégse** pair, like the title editor beside it, AND an
   * outside click that still puts the editor away. They are not in conflict — the buttons are for
   * somebody who wants to be sure, the blur is for somebody who has already moved on — so the two
   * are separate props now and `footer` defaults to the old coupling.
   *
   * ⚠️ **With a footer, the primary button runs `commit`, not `send`.** `send` refuses an empty
   * document, which is right for a comment and wrong for a field: clearing a description IS the
   * edit, and a Save that does nothing when the box is empty is the bug this item names.
   */
  readonly footer?: boolean;
  /**
   * ⚠️ **Files the caller has already collected — a drop on the surface that landed over the
   * comment box.** They arrive staged, exactly as though the paperclip had been used, so the
   * person writes the comment the file is about rather than posting it bare.
   *
   * ⚠️ **Read once per identity, not merged on every render.** The caller clears it after handing
   * it over; an effect that appended on every render would multiply one drop into many.
   */
  readonly incomingFiles?: readonly File[] | null;
  /** Called once `incomingFiles` has been taken, so the caller can clear it. */
  readonly onIncomingTaken?: () => void;
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ A FIELD INSIDE A FORM — ux-pass-2, the invoice's Megjegyzés.     │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * Given, the composer is one field of a dialog that has its own Save: every change is reported
   * here (`''` when the document is empty — never `<p></p>`), and ⌘/Ctrl+Enter does nothing,
   * because `send` would post the text somewhere and then CLEAR the box. Pass `footer={false}` with
   * it, so no Send button competes with the dialog's.
   */
  readonly onChange?: (html: string) => void;
  /** Already translated. Defaults to the comment thread's *Írj megjegyzést…*. */
  readonly placeholder?: string;
  /** The editor's accessible name. Defaults to the comment thread's. */
  readonly label?: string;
}) {
  const t = useT();
  const [query, setQuery] = useState<string | null>(null);
  /** Who has been picked. Pruned against the text at submit — see below. */
  const [picked, setPicked] = useState<readonly WorkspaceMember[]>([]);
  /* ⚠️ A ref, because the editor's own update handler reads it and a stale closure would prune
   * against whoever was picked one keystroke ago. */
  const pickedRef = useRef<readonly WorkspaceMember[]>([]);
  pickedRef.current = picked;

  /**
   * ⚠⚠️ **C22 · STAGED, NOT UPLOADED, AND THE ORDER IS WHY.** A file is linked to a comment,
   * so there is nothing to link it to until the comment exists. Uploading here would need a
   * comment id this component cannot have; the files ride along with the submit and the write
   * sequences them (`task-route.tsx`).
   *
   * ⚠️ It shipped without this and the attach control lived only UNDER a saved comment — which
   * is where the files end up, and not where anybody looks for the button while writing one.
   */
  const [staged, setStaged] = useState<readonly File[]>([]);

  /* ⚠️ Keyed on the array's identity: the caller hands a NEW array per drop and clears it after,
     so this runs exactly once per drop. `onIncomingTaken` is reached through a ref-free closure
     because it is called in the same tick the effect runs. */
  useEffect(() => {
    if (incomingFiles === undefined || incomingFiles === null || incomingFiles.length === 0) return;
    setStaged((current) => [...current, ...incomingFiles]);
    onIncomingTaken?.();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `onIncomingTaken` is an inline arrow
       at the call site; including it re-runs this on every render and re-stages the same files. */
  }, [incomingFiles]);
  const picker = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      /**
       * ⚠️⚠️ **THE STARTER KIT IS TRIMMED TO WHAT `sanitiseHtml` ALLOWS.** Its defaults include
       * `horizontalRule` and `heading` at all six levels; the server keeps neither `<hr>` nor
       * `<h1>`, so a control for them would write markup the save strips — a button that appears
       * to work and does not.
       */
      StarterKit.configure({
        horizontalRule: false,
        heading: { levels: [2, 3] },
        /* Its own link extension is replaced below, so the two do not both register the mark. */
        link: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        /* ⚠️ The same scheme set the server enforces. The editor refusing it first means somebody
         * learns at the toolbar rather than from a link that silently vanished on save. */
        protocols: ['http', 'https', 'mailto'],
      }),
      Placeholder.configure({ placeholder: placeholder ?? t('task.comment.placeholder') }),
    ],
    /* ⚠️ Seeded for an EDIT — item 73. `useEditor` reads this once at creation, which is right:
       a `content` that tracked a prop would replace what somebody was typing. */
    content: initialHtml ?? '',
    /**
     * ⚠️ **Created synchronously, which is why nothing below guards for a null editor.** TipTap v3
     * types `useEditor` as returning an `Editor` and defers creation only for SSR; this app has no
     * server render, so stating it makes the type honest rather than relying on a default.
     */
    immediatelyRender: true,
    /**
     * ⚠️⚠️ **WITHOUT THIS THE TOOLBAR IS FROZEN AT ITS MOUNT STATE.** TipTap v3 does not re-render
     * the host component on a transaction, for performance — so `editor.isEmpty`, every
     * `editor.isActive(...)` and `editor.can().undo()` were read once and never again. In a browser
     * that meant: type a whole comment and **Send stays disabled**, bold never lights up, undo
     * never enables. Nothing errors, and jsdom cannot see it because it cannot type at all.
     *
     * The toolbar is small and re-rendering it per keystroke is cheap; a frozen toolbar is not.
     */
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: {
        /* `prose`-less on purpose: the styles are the product's own, set on the wrapper below. */
        class: 'min-h-[92px] px-3 py-2 outline-none',
        /**
         * ⚠️⚠️ **THE ROLE IS EXPLICIT, BECAUSE `contenteditable` DOES NOT CARRY ONE.** The
         * HTML-to-ARIA mapping gives a contenteditable div no implicit role, so assistive
         * technology meets an unlabelled group rather than a text field — and
         * `getByRole('textbox')` finds nothing, which is how this was noticed.
         */
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': label ?? t('task.comment.label'),
      },
    },
  });

  const matches = query === null ? [] : mentionMatches(people, query);

  /**
   * What leaving the field does.
   *
   * ⚠️⚠️ **AN EMPTIED FIELD MUST STILL COMMIT, WHICH `send` REFUSES TO DO.** `send` returns early
   * on an empty document because a blank comment is nothing to post — right for a comment and
   * exactly wrong here, where clearing the description IS the edit. So an inline field that was
   * emptied submits the empty HTML and lets the caller turn it into `null`.
   *
   * ⚠️ **An untouched field does nothing at all.** Clicking in and straight back out must not write
   * a version bump, an audit row and a *saved* toast for an edit nobody made.
   */
  const commit = (): void => {
    if (editor.isEmpty) {
      if (initialHtml !== undefined && initialHtml !== '') onSubmit('', [], []);
      else onCancel?.();
      return;
    }
    if (editor.getHTML() === initialHtml) {
      onCancel?.();
      return;
    }
    send();
  };

  const send = (): void => {
    /**
     * ⚠️ **`isEmpty` rather than a trimmed string.** An untouched ProseMirror document serialises
     * to `<p></p>`, which is not empty by any string test and is empty by every other measure —
     * and the server would refuse it with `validation_failed` after the round trip.
     */
    if (editor.isEmpty) return;

    const kept = keptMentions(editor.getText(), pickedRef.current);

    onSubmit(
      editor.getHTML(),
      kept.map((person) => person.id),
      staged,
    );
    editor.commands.clearContent(true);
    setPicked([]);
    setStaged([]);
    setQuery(null);
  };

  /** Replace the `@fragment` under the caret with the chosen name. */
  const choose = (person: WorkspaceMember): void => {
    if (query === null) return;
    /**
     * ⚠️⚠️ **THE RANGE IS FOUND IN THE DOCUMENT, NOT COMPUTED FROM `query.length`.** It was
     * `from - (query.length + 1)`, and a real browser showed why that is wrong: picking after
     * typing `@Fod` left `@F@Fodor Anna` in the box. The picker's `query` is React state written by
     * an editor event, so at the moment of the click it can be one keystroke behind what is in the
     * document — and the deletion is then short by however far behind it is.
     *
     * Asking the document removes the second source of truth: `parentOffset - at` is exactly how
     * many characters lie between the `@` and the caret, and inside one text block a ProseMirror
     * position advances one per character.
     */
    const { $from } = editor.state.selection;
    const before = $from.parent.textBetween(0, $from.parentOffset);
    const at = before.lastIndexOf('@');
    if (at === -1) return;

    editor
      .chain()
      .focus()
      .deleteRange({ from: $from.pos - ($from.parentOffset - at), to: $from.pos })
      .insertContent(`@${person.displayName} `)
      .run();

    setPicked((current) =>
      current.some((entry) => entry.id === person.id) ? current : [...current, person],
    );
    setQuery(null);
  };

  /**
   * The mention picker follows the caret.
   *
   * ⚠️⚠️ **SUBSCRIBED WITH `editor.on`, NOT PASSED AS AN `onUpdate` OPTION — AND THE OPTION DID
   * NOTHING.** TipTap v3 binds the options object once at creation, so a handler declared there
   * never ran: typing produced markup, the picker never opened, and nothing errored. Found by
   * logging inside it and seeing no output at all.
   *
   * ⚠️ **It reads the editor's PLAIN TEXT and its caret**, not its HTML. The mention contract is
   * unchanged from the textarea: what has to be true is that `@Name` is in the text, because that
   * is what the server's ids are checked against and what the renderer highlights.
   */
  useEffect(() => {
    const follow = (): void => {
      const text = editor.getText();
      /* ProseMirror positions count the document node, so the caret is one ahead of the string. */
      const caret = Math.max(0, Math.min(editor.state.selection.from - 1, text.length));
      setQuery(mentionQuery(text, caret));
    };

    editor.on('update', follow);
    editor.on('selectionUpdate', follow);
    return () => {
      editor.off('update', follow);
      editor.off('selectionUpdate', follow);
    };
  }, [editor]);

  /* ⚠️ A field in a form reports every change — see `onChange`. Through a ref, so an inline arrow at
     the call site does not re-subscribe on every render. */
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    const report = (): void => {
      onChangeRef.current?.(editor.isEmpty ? '' : editor.getHTML());
    };
    editor.on('update', report);
    return () => {
      editor.off('update', report);
    };
  }, [editor]);

  /* ⚠️ Torn down on unmount, or ProseMirror keeps the DOM and its plugins alive behind the
   * closed drawer — one leaked editor per task somebody opens. */
  useEffect(
    () => () => {
      editor.destroy();
    },
    [editor],
  );

  return (
    <div
      className="relative"
      /**
       * ⚠️ **`onBlurCapture`, because React's `onBlur` IS `focusout`** — it bubbles, unlike the
       * native `blur` — and capture runs before anything inside can stop it. `relatedTarget` is
       * where focus WENT: inside this element means the toolbar or the mention picker took it and
       * the edit is still in progress; outside, or null (a click on the page background), means
       * the person has left the field.
       */
      onBlurCapture={(event) => {
        if (!commitOnBlur) return;
        const next = event.relatedTarget;
        if (next !== null && event.currentTarget.contains(next)) return;
        commit();
      }}
      onKeyDown={(event) => {
        /* ⚠️ Escape reverts, and `stopPropagation` keeps it from also closing the drawer the
           editor is sitting in — one Escape, one effect. */
        if (!commitOnBlur || event.key !== 'Escape') return;
        event.stopPropagation();
        onCancel?.();
      }}
    >
      <div className="focus-within:ring-ring/40 overflow-hidden rounded-lg border focus-within:ring-2">
        <Toolbar editor={editor} t={t} />

        {/**
         * ⚠️ **The block markup is styled HERE and nowhere else.** TipTap ships no CSS, and the
         * app's reset flattens `h2`, `ul` and `blockquote` — so without these the editor would show
         * a heading and a bullet list as plain paragraphs, which is the *"formatting shown
         * immediately"* half of the ask failing quietly.
         *
         * ⚠️ The same rules style the SAVED comment in `task-detail.tsx`. Two sets would let what
         * you typed and what you read afterwards drift apart.
         */}
        <div
          className={`text-[12.5px] ${COMMENT_PROSE}`}
          onKeyDown={(event) => {
            /* ⚠️ ⌘/Ctrl+Enter sends. Plain Enter is a paragraph break — in an editor it cannot be
             * the submit key, and Jira does the same. */
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              /* A field in a form does not send — the dialog's Save does. */
              if (onChange !== undefined) return;
              send();
            }
          }}
        >
          <EditorContent editor={editor} />
        </div>
      </div>

      {matches.length === 0 ? null : (
        <ul
          /* ⚠️ A listbox of options, not a list of buttons: this is a choice among a fixed set
             offered in response to typing, which is what the role means. */
          role="listbox"
          className="bg-popover absolute bottom-full z-20 mb-1 w-[240px] overflow-hidden rounded-md border p-1 shadow-md"
          aria-label={t('task.comment.mentions')}
        >
          {matches.map((person) => (
            <li key={person.id} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-[12.5px]"
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => {
                  choose(person);
                }}
              >
                <Icon name="at-sign" size={12} className="text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">{person.displayName}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {staged.length === 0 ? null : (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {staged.map((file, index) => (
            <li
              key={`${file.name}-${String(index)}`}
              className="bg-muted flex items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px]"
            >
              <Icon name="paperclip" size={12} className="text-muted-foreground" />
              <span className="max-w-[180px] truncate">{file.name}</span>
              <button
                type="button"
                aria-label={t('task.comment.attachRemove', { name: file.name })}
                className="text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setStaged((current) => current.filter((_, at) => at !== index));
                }}
              >
                <Icon name="x" size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* ⚠️ No footer in an inline field UNLESS it asks for one — no Save, no Cancel, and no
       *⌘+Enter to send* hint for a control that does not send. Item 3, then item 13. */}
      <div className={(footer ?? !commitOnBlur) ? 'mt-2 flex items-center gap-2' : 'hidden'}>
        {allowFiles ? (
          <>
            {/* ⚠️ Multiple, because "here are the three screenshots" is one action to a person. */}
            <input
              ref={picker}
              type="file"
              multiple
              className="hidden"
              onChange={(event) => {
                const chosen = [...(event.target.files ?? [])];
                /* Cleared, so choosing the same file twice still fires `change`. */
                event.target.value = '';
                if (chosen.length > 0) setStaged((current) => [...current, ...chosen]);
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                picker.current?.click();
              }}
            >
              <Icon name="paperclip" size={14} />
              {t('task.attachment.add')}
            </Button>
          </>
        ) : null}
        {/* ⚠️ The send hint belongs to a control that SENDS — a field saves. */}
        {commitOnBlur ? null : (
          <span className="text-muted-foreground text-[11.5px]">{t('task.comment.hint')}</span>
        )}
        <div className="flex-1" />
        {onCancel === undefined ? null : (
          <Button variant="outline" size="sm" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        )}
        {/* ⚠️⚠️ **A FIELD'S SAVE IS NEVER DISABLED, AND THAT IS ITEM 13'S SECOND HALF.** An empty
            comment is nothing to post; an empty description is somebody deleting one. `commit`
            knows the difference — `send` does not, and would sit there greyed out. */}
        <Button
          size="sm"
          disabled={commitOnBlur ? false : editor.isEmpty}
          onClick={commitOnBlur ? commit : send}
        >
          {submitLabel ?? t('task.comment.send')}
        </Button>
      </div>
    </div>
  );
}
